import { useEffect } from 'react';
import _ from 'lodash';
// import { toast } from 'react-toastify';
import { useRecoilState, useSetRecoilState, useRecoilValue } from 'recoil';
import { ICompanySummary, IListStatistics, IUserListRef } from '../types/company';
import useApiCaller from '../hooks/use-api-caller';
import {
  companiesErrorAtom,
  companiesAtom,
  companiesRefreshCounterAtom,
  loadingCompaniesAtom,
  userListsAtom,
  userListStatsAtom,
} from '../store/portfolio';
import { addSortFieldToCompanies } from '../utils/add-sort-field-to-companies';
import { createUserListStats } from '../utils/user-list-functions';
import IConfig from '../types/config';

export default function SharedData({
  configStored,
}: Readonly<{
  configStored: IConfig | null;
}>) {
  const apiCaller = useApiCaller();

  const [loadingCompanies, setLoadingCompanies] = useRecoilState<boolean>(loadingCompaniesAtom);
  const setCompaniesError = useSetRecoilState<boolean>(companiesErrorAtom);
  const [companies, setCompanies] = useRecoilState<ICompanySummary[]>(companiesAtom);
  const companiesRefreshCounter = useRecoilValue<number>(companiesRefreshCounterAtom);
  const setUserLists = useSetRecoilState<IUserListRef[]>(userListsAtom);
  const setUserListStats = useSetRecoilState<IListStatistics[]>(userListStatsAtom);

  const userLists = useRecoilValue<IUserListRef[]>(userListsAtom);

  const normalisePortfolioLists = (items: ICompanySummary[]) => {
    const userListsById: Record<number, IUserListRef> = {};

    const companiesNormalised: ICompanySummary[] = items.map((company) => {
      const lists = (company.lists ?? [])
        .filter((l): l is IUserListRef => !!l && typeof l.id === 'number')
        .map((l) => {
          userListsById[l.id] = { id: l.id, name: l.name };
          return { id: l.id, name: l.name };
        });

      return {
        ...company,
        lists: _.uniqBy(lists, 'id'),
      };
    });

    const userLists = _.orderBy(
      Object.keys(userListsById).map((k) => userListsById[Number(k)]),
      [(list) => _.toLower(list.name ?? '')],
      ['asc']
    );

    return { companiesNormalised: companiesNormalised, userLists };
  };

  const fetchPortfolio = () => {
    if (loadingCompanies) return;

    setLoadingCompanies(true);

    apiCaller
      .getPortfolio()
      .then((res) => {
        const withSortField = addSortFieldToCompanies(res.items);
        const { companiesNormalised, userLists } = normalisePortfolioLists(withSortField);

        const localCompanies = _.orderBy(companiesNormalised, 'current_score', 'desc');
        setCompanies(localCompanies);
        setUserLists(userLists);
      })
      .catch((err) => {
        console.error(err);
        setCompaniesError(true);
      })
      .finally(() => {
        setLoadingCompanies(false);
      });
  };

  useEffect(() => {
    if (configStored) {
      fetchPortfolio();
    }
  }, [configStored, companiesRefreshCounter]);

  //every time companies list changes, recalculate dependent lists
  useEffect(() => {
    setUserListStats(createUserListStats(userLists, companies));
  }, [companies, userLists, setUserListStats]);

  return <div></div>;
}
